import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Edit3, Trash2, Eye, EyeOff, ImagePlus,
  Loader2, Check, RefreshCw, X
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const ManageMealsPanel = ({ adminToken }) => {
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | deactivated
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [missingImages, setMissingImages] = useState(null);

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/dishes?token=${adminToken}`);
      if (res.data.success) setMeals(res.data.dishes);
    } catch (err) { toast.error("Failed to load meals"); }
    setLoading(false);
  };

  const checkMissingImages = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/missing-images?token=${adminToken}`);
      if (res.data.success) setMissingImages(res.data);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { fetchMeals(); checkMissingImages(); }, []);

  const toggleActive = async (mealId) => {
    try {
      const res = await axios.patch(`${API}/api/admin/dishes/${mealId}/toggle-active?token=${adminToken}`);
      if (res.data.success) {
        setMeals(prev => prev.map(m => m.meal_id === mealId ? { ...m, is_active: res.data.is_active } : m));
        toast.success(res.data.is_active ? "Meal activated" : "Meal deactivated");
      }
    } catch (err) { toast.error("Failed to toggle"); }
  };

  const deleteMeal = async (meal) => {
    if (!window.confirm(`Permanently delete "${meal.meal_name}"? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API}/api/admin/dishes/${meal.meal_id}?token=${adminToken}`);
      if (res.data.success) {
        setMeals(prev => prev.filter(m => m.meal_id !== meal.meal_id));
        toast.success(res.data.message);
      }
    } catch (err) { toast.error("Failed to delete"); }
  };

  const startEdit = (meal) => {
    setEditingId(meal.meal_id);
    setEditData({
      meal_name: meal.meal_name || "",
      primary_meal_type: meal.primary_meal_type || "Lunch",
      secondary_meal_type: meal.secondary_meal_type || "",
      diet_type: meal.diet_type || "Veg",
      is_gluten_free: meal.is_gluten_free || false,
      calories: meal.calories || 0,
      protein: meal.protein || 0,
      prep_time_min: meal.prep_time_min || 30,
      cuisine: meal.cuisine || "Indian",
      base: meal.base || "Other",
      complexity: meal.complexity || "Easy",
      meal_nature: meal.meal_nature || "Balanced",
      notes: meal.notes || "",
      ingredients: (meal.ingredients || []).join("\n"),
      steps: (meal.steps || []).join("\n"),
      image_url: meal.image_url || "",
    });
  };

  const saveEdit = async (mealId) => {
    setSaving(true);
    try {
      const payload = { ...editData };
      delete payload.image_url;
      payload.ingredients = payload.ingredients.split("\n").filter(l => l.trim());
      payload.steps = payload.steps.split("\n").filter(l => l.trim());
      payload.calories = parseInt(payload.calories) || 0;
      payload.protein = parseInt(payload.protein) || 0;
      payload.prep_time_min = parseInt(payload.prep_time_min) || 30;
      if (!payload.secondary_meal_type) payload.secondary_meal_type = null;

      const res = await axios.put(`${API}/api/admin/dishes/${mealId}?token=${adminToken}`, payload);
      if (res.data.success) {
        setMeals(prev => prev.map(m => m.meal_id === mealId ? { ...m, ...res.data.meal } : m));
        setEditingId(null);
        toast.success("Meal updated");
      }
    } catch (err) { toast.error("Failed to save"); }
    setSaving(false);
  };

  const replaceImage = async (mealId, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/admin/bulk-upload-image?token=${adminToken}&meal_id=${mealId}`, fd);
      if (res.data.success) {
        setMeals(prev => prev.map(m => m.meal_id === mealId ? { ...m, image_url: res.data.image_url } : m));
        setEditData(p => ({ ...p, image_url: res.data.image_url }));
        toast.success("Image replaced");
      }
    } catch (err) { toast.error("Image upload failed"); }
  };

  // Filter + sort
  const filtered = meals.filter(m => {
    if (statusFilter === "active" && m.is_active === false) return false;
    if (statusFilter === "deactivated" && m.is_active !== false) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return m.meal_name?.toLowerCase().includes(q) || m.meal_id?.toLowerCase().includes(q) || m.diet_type?.toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    const aActive = a.is_active !== false ? 0 : 1;
    const bActive = b.is_active !== false ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    return (a.meal_name || "").localeCompare(b.meal_name || "");
  });

  const activeCount = meals.filter(m => m.is_active !== false).length;
  const deactivatedCount = meals.filter(m => m.is_active === false).length;
  const missingNatureCount = meals.filter(m => !m.meal_nature).length;

  const autoClassifyNature = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/classify-meal-nature?token=${adminToken}`);
      if (res.data.success) {
        toast.success(`Classified ${res.data.updated} meals`);
        fetchMeals();
      }
    } catch (err) { toast.error("Classification failed"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="manage-meals-panel">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input type="text" placeholder="Search meals..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-xl bg-white" data-testid="manage-search" />
        </div>
        <Button size="sm" variant="outline" onClick={fetchMeals} className="gap-1 text-xs h-9">
          <RefreshCw size={12} />Refresh
        </Button>
      </div>

      {/* Missing meal_nature banner */}
      {missingNatureCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-between" data-testid="missing-nature-banner">
          <div>
            <p className="text-sm font-medium text-amber-700">{missingNatureCount} meal(s) missing Meal Nature</p>
            <p className="text-[10px] text-amber-600 mt-0.5">Auto-classify based on calories and dish type</p>
          </div>
          <Button size="sm" onClick={autoClassifyNature} className="text-xs h-8 gap-1 bg-amber-600 hover:bg-amber-700" data-testid="auto-classify-btn">
            Auto-classify
          </Button>
        </div>
      )}

      {/* Missing images banner */}
      {missingImages && missingImages.images_missing > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="missing-images-banner">
          <div className="flex items-center gap-2 mb-2">
            <ImagePlus size={14} className="text-red-500" />
            <p className="text-sm font-medium text-red-700">{missingImages.images_missing} meal(s) missing images</p>
          </div>
          <div className="space-y-1">
            {missingImages.missing_meals.map(m => (
              <div key={m.meal_id} className="flex items-center justify-between text-xs text-red-600 bg-red-100/50 rounded-lg px-2.5 py-1.5">
                <span className="font-medium">{m.meal_name}</span>
                <button onClick={() => { setSearch(m.meal_name); startEdit(meals.find(x => x.meal_id === m.meal_id) || m); }}
                  className="text-red-700 underline hover:no-underline" data-testid={`fix-img-${m.meal_id}`}>Fix</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status filter */}
      <div className="flex gap-2 items-center" data-testid="manage-filters">
        {[
          { key: "all", label: `All (${meals.length})` },
          { key: "active", label: `Active (${activeCount})` },
          { key: "deactivated", label: `Deactivated (${deactivatedCount})` },
        ].map(f => (
          <button key={f.key} onClick={() => setStatusFilter(f.key)}
            className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
              statusFilter === f.key
                ? f.key === "deactivated" ? "bg-red-100 text-red-700 border border-red-200" : "bg-primary/10 text-primary border border-primary/20"
                : "bg-white text-muted-foreground border border-border hover:bg-muted/50"
            }`} data-testid={`filter-${f.key}`}>
            {f.label}
          </button>
        ))}
        <span className="text-xs text-muted-foreground ml-auto">{sorted.length} shown</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-border/40 overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_90px_60px_60px_60px_70px_55px_100px] gap-2 px-4 py-2.5 bg-muted/30 border-b border-border/20 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          <span>Meal</span>
          <span>Type</span>
          <span>Diet</span>
          <span>Cal</span>
          <span>Prot</span>
          <span>Nature</span>
          <span>Status</span>
          <span className="text-right">Actions</span>
        </div>

        {/* Rows */}
        <div className="divide-y divide-border/10">
          {sorted.map(meal => {
            const isActive = meal.is_active !== false;
            const isEditing = editingId === meal.meal_id;

            return (
              <div key={meal.meal_id} data-testid={`manage-meal-${meal.meal_id}`}>
                {/* Row */}
                <div className={`grid grid-cols-[1fr_90px_60px_60px_60px_70px_55px_100px] gap-2 px-4 py-2.5 items-center text-sm ${!isActive ? "opacity-50 bg-red-50/30" : "hover:bg-muted/20"}`}>
                  <span className="font-medium text-sm truncate">{meal.meal_name}</span>
                  <span className="text-[11px]">
                    <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">{meal.primary_meal_type}</span>
                  </span>
                  <span className={`text-[11px] px-1.5 py-0.5 rounded inline-block w-fit ${
                    meal.diet_type === "Veg" ? "bg-green-50 text-green-700" :
                    meal.diet_type === "Non-Veg" ? "bg-red-50 text-red-700" :
                    meal.diet_type === "Egg" ? "bg-amber-50 text-amber-700" :
                    "bg-green-50 text-green-700"
                  }`}>{meal.diet_type || "Veg"}</span>
                  <span className="text-xs text-muted-foreground">{meal.calories}</span>
                  <span className="text-xs text-muted-foreground">{meal.protein}g</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded inline-block w-fit ${
                    meal.meal_nature === "Light" ? "bg-green-50 text-green-700" :
                    meal.meal_nature === "Indulgent" ? "bg-orange-50 text-orange-700" :
                    "bg-slate-50 text-slate-600"
                  }`}>{meal.meal_nature || "—"}</span>
                  <span>{isActive
                    ? <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                    : <span className="text-[10px] text-red-500 font-medium">Off</span>
                  }</span>
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => { isEditing ? setEditingId(null) : startEdit(meal); }}
                      className={`h-7 w-7 p-0 ${isEditing ? "bg-primary/10" : ""}`} data-testid={`edit-btn-${meal.meal_id}`}>
                      {isEditing ? <X size={13} className="text-primary" /> : <Edit3 size={13} className="text-muted-foreground" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(meal.meal_id)}
                      className="h-7 w-7 p-0" data-testid={`toggle-btn-${meal.meal_id}`}>
                      {isActive ? <EyeOff size={13} className="text-amber-500" /> : <Eye size={13} className="text-green-500" />}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteMeal(meal)}
                      className="h-7 w-7 p-0" data-testid={`delete-btn-${meal.meal_id}`}>
                      <Trash2 size={13} className="text-red-400" />
                    </Button>
                  </div>
                </div>

                {/* Edit form */}
                {isEditing && (
                  <div className="px-4 py-4 border-t border-border/20 bg-muted/10 space-y-3">
                    {/* Image section */}
                    <div className="flex items-center gap-4 pb-3 border-b border-border/10">
                      <div className="w-20 h-20 rounded-xl overflow-hidden bg-muted/30 flex-shrink-0">
                        <img src={`${API}${editData.image_url || meal.image_url}`} alt=""
                          className="w-full h-full object-cover"
                          onError={e => { e.target.src = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=100"; }} />
                      </div>
                      <div>
                        <p className="text-xs font-medium mb-1.5">Meal Image</p>
                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors text-xs">
                          <ImagePlus size={12} />Replace Image
                          <input type="file" accept="image/jpeg,image/png" className="hidden"
                            onChange={e => replaceImage(meal.meal_id, e.target.files[0])} data-testid={`replace-img-${meal.meal_id}`} />
                        </label>
                        <p className="text-[10px] text-muted-foreground mt-1">JPG or PNG, auto-converted to JPG</p>
                      </div>
                    </div>

                    {/* Fields */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Meal Name</label>
                        <input className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.meal_name} onChange={e => setEditData(p => ({ ...p, meal_name: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Primary Type</label>
                        <select className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.primary_meal_type} onChange={e => setEditData(p => ({ ...p, primary_meal_type: e.target.value }))}>
                          <option>Breakfast</option><option>Lunch</option><option>Dinner</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Secondary Type</label>
                        <select className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.secondary_meal_type || ""} onChange={e => setEditData(p => ({ ...p, secondary_meal_type: e.target.value }))}>
                          <option value="">None</option><option>Breakfast</option><option>Lunch</option><option>Dinner</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Diet Type</label>
                        <select className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.diet_type} onChange={e => setEditData(p => ({ ...p, diet_type: e.target.value }))}>
                          <option>Veg</option><option>Non-Veg</option><option>Egg</option><option>Vegan</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Calories</label>
                        <input type="number" className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.calories} onChange={e => setEditData(p => ({ ...p, calories: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Protein (g)</label>
                        <input type="number" className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.protein} onChange={e => setEditData(p => ({ ...p, protein: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Prep Time (min)</label>
                        <input type="number" className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.prep_time_min} onChange={e => setEditData(p => ({ ...p, prep_time_min: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Cuisine</label>
                        <input className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.cuisine} onChange={e => setEditData(p => ({ ...p, cuisine: e.target.value }))} />
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Base</label>
                        <select className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.base} onChange={e => setEditData(p => ({ ...p, base: e.target.value }))}>
                          <option>Rice</option><option>Roti</option><option>Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Meal Nature</label>
                        <select className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                          value={editData.meal_nature} onChange={e => setEditData(p => ({ ...p, meal_nature: e.target.value }))}>
                          <option>Light</option><option>Balanced</option><option>Indulgent</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-0.5">Notes</label>
                      <input className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white"
                        value={editData.notes} onChange={e => setEditData(p => ({ ...p, notes: e.target.value }))} />
                    </div>

                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-0.5">Ingredients (one per line)</label>
                      <textarea className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white resize-none" rows={4}
                        value={editData.ingredients} onChange={e => setEditData(p => ({ ...p, ingredients: e.target.value }))} />
                    </div>

                    <div>
                      <label className="text-[10px] text-muted-foreground block mb-0.5">Steps (one per line)</label>
                      <textarea className="w-full text-xs border border-border rounded-lg px-2.5 py-1.5 bg-white resize-none" rows={4}
                        value={editData.steps} onChange={e => setEditData(p => ({ ...p, steps: e.target.value }))} />
                    </div>

                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}
                        className="text-xs h-8 px-4">Cancel</Button>
                      <Button size="sm" onClick={() => saveEdit(meal.meal_id)} disabled={saving}
                        className="text-xs h-8 px-4 gap-1" data-testid={`save-btn-${meal.meal_id}`}>
                        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}Save
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {sorted.length === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {search ? "No meals match your search" : statusFilter === "deactivated" ? "No deactivated meals" : "No meals in database"}
        </div>
      )}
    </div>
  );
};

export default ManageMealsPanel;
